import { useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { scrollToId } from '../lib/smoothScroll';

const BrowserFrame = ({ src, alt }) => (
  <div
    role="img"
    aria-label={alt}
    className="overflow-hidden rounded-xl border border-[#DCE2DC] bg-white shadow-[0_40px_80px_-32px_rgba(11,31,26,0.35)]"
  >
    <div className="flex items-center gap-1.5 border-b border-[#E5E7EB] bg-[#F4F5F1] px-4 py-2.5" aria-hidden="true">
      <span className="h-2.5 w-2.5 rounded-full bg-[#D8DDD6]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#D8DDD6]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#D8DDD6]" />
      <span className="ml-3 hidden rounded-md bg-white px-3 py-1 font-op text-[10px] tracking-wide text-[#6B7280] sm:block">
        app.relaybyavenir.com/queues
      </span>
    </div>
    <img src={src} alt={alt} className="block h-auto w-full" fetchpriority="high" decoding="async" />
  </div>
);

const FLEET_IMG = '/assets/fleet-aerial.jpg';

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: '112%' }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const yPhone = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -46]);
  const yDesk = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 30]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-[68px]" data-testid="hero-section">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[720px] select-none opacity-[0.22] sm:h-[460px] sm:opacity-[0.30]"
        style={{
          backgroundImage: `url(${FLEET_IMG})`,
          backgroundSize: '150% auto',
          backgroundPosition: 'center 55%',
          maskImage: 'linear-gradient(to bottom, transparent 0%, black 42%, black 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 42%, black 100%)',
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 select-none"
        style={{
          background:
            'linear-gradient(to right, rgba(250,250,247,0.98) 4%, rgba(250,250,247,0.88) 30%, rgba(250,250,247,0.45) 48%, transparent 62%)',
        }}
      />
      <div className="container-relay relative grid grid-cols-1 items-center gap-14 pb-24 pt-14 sm:pt-20 lg:grid-cols-12 lg:gap-8 lg:pb-32 lg:pt-24">
        <div className="lg:col-span-6 xl:col-span-5">
          <motion.p
            className="eyebrow mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            data-testid="hero-eyebrow"
          >
            Built around real operations
          </motion.p>
          <h1 className="text-[40px] font-extrabold leading-[1.05] tracking-[-0.03em] text-[#111111] sm:text-5xl lg:text-[52px] xl:text-6xl" data-testid="hero-headline">
            <MaskedLine delay={0.25}>Fleet operations</MaskedLine>
            <MaskedLine delay={0.37}>
              software, <span className="text-[#0F5C4A]">built around</span>
            </MaskedLine>
            <MaskedLine delay={0.49}>
              <span className="text-[#0F5C4A]">the operation.</span>
            </MaskedLine>
          </h1>
          <motion.p
            className="mt-6 text-lg font-semibold tracking-tight text-[#111111]"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            data-testid="hero-subheadline"
          >
            Make the human touch more effective.
          </motion.p>
          <motion.p
            className="mt-4 max-w-[46ch] text-base leading-relaxed text-[#6B7280]"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.88 }}
            data-testid="hero-body"
          >
            Relay connects drivers, fleet managers, account teams and suppliers through one operational service
            layer. Routine updates move automatically. Exceptions reach the right people. Every action strengthens
            the vehicle record.
          </motion.p>
          <motion.div
            className="mt-9 flex flex-col gap-3 sm:flex-row"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
          >
            <button data-testid="hero-cta-demo" onClick={() => scrollToId('platform')} className="btn-primary">
              See Relay in action <ArrowRight size={16} />
            </button>
            <button data-testid="hero-cta-talk" onClick={() => scrollToId('enquiry')} className="btn-secondary">
              Talk to us
            </button>
          </motion.div>
        </div>
        <div className="relative lg:col-span-6 xl:col-span-7">
          <motion.div
            style={{ y: yDesk }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <BrowserFrame
              src="/assets/relay-desktop-workspace.webp"
              alt="Relay operations workspace showing operational queues, SLA statistics, deadline filters and the vehicle work list"
            />
          </motion.div>
          <motion.div
            style={{ y: yPhone }}
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -bottom-10 left-2 w-[150px] sm:-left-6 sm:w-[190px] lg:-bottom-14"
          >
            <img
              src="/assets/relay-phone.png"
              alt="Relay mobile app dashboard showing open requests, confirmations awaiting action and the live requests list"
              fetchpriority="high"
              decoding="async"
              className="block h-auto w-full drop-shadow-[0_36px_48px_rgba(11,31,26,0.35)]"
              data-testid="hero-phone"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
